/**
 * 重み付きミックス（型の配分・画像スタイルの配分）の純粋関数。
 * 設定画面の2つの配分 UI で共通に使う（従来はほぼ同じ処理が二重に書かれていた — audit E3）
 */

export interface MixEntry {
  key: string
  weight: number
}

/** 均等割りで合計100にする（端数は先頭に寄せる） */
export function equalSplit(n: number): number[] {
  if (n <= 0) return []
  const base = Math.floor(100 / n)
  return Array.from({ length: n }, (_, i) => base + (i === 0 ? 100 - base * n : 0))
}

export function equalWeights(keys: string[]): MixEntry[] {
  const w = equalSplit(keys.length)
  return keys.map((key, i) => ({ key, weight: w[i] }))
}

/** 合計がちょうど 100 になるよう整数に丸める（最大剰余法）。raw の合計は 100 を想定 */
function roundTo100(raw: number[]): number[] {
  const floors = raw.map(v => Math.floor(v))
  let rest = 100 - floors.reduce((a, b) => a + b, 0)
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)
  for (const { i } of order) {
    if (rest <= 0) break
    floors[i] += 1
    rest -= 1
  }
  return floors
}

/**
 * 選択が変わったときの重みの再配分。
 * - 既存の項目は互いの比率を保つ（ユーザーが調整した配分を壊さない）
 * - 新しく足した項目は均等割り相当（100/n）を受け取り、残りを既存で比例配分
 * - 外した項目の分は残りの項目へ比例配分
 * 結果は nextKeys の順で、合計は必ず 100。
 */
export function rebalance(prev: MixEntry[], nextKeys: string[]): MixEntry[] {
  const n = nextKeys.length
  if (n === 0) return []
  const prevWeight = new Map(prev.map(m => [m.key, m.weight]))
  const kept = nextKeys.filter(k => prevWeight.has(k))
  const addedCount = n - kept.length
  const share = 100 / n
  const remaining = 100 - share * addedCount
  const keptSum = kept.reduce((a, k) => a + Math.max(0, prevWeight.get(k) ?? 0), 0)
  const raw = nextKeys.map(k => {
    if (!prevWeight.has(k)) return share
    if (keptSum <= 0) return remaining / kept.length
    return (Math.max(0, prevWeight.get(k) ?? 0) * remaining) / keptSum
  })
  const rounded = roundTo100(raw)
  return nextKeys.map((key, i) => ({ key, weight: rounded[i] }))
}

/**
 * カードのトグル。選択中なら外し、未選択なら末尾に足す。
 * 最後の1つを外そうとした場合は null（外させない）
 */
export function toggleKey(selected: string[], key: string): string[] | null {
  if (selected.includes(key)) {
    if (selected.length === 1) return null
    return selected.filter(k => k !== key)
  }
  return [...selected, key]
}

/**
 * 単一選択（single）＋配分（mix）の組を、トグル後の状態に更新する。
 * 1つだけなら mix は空（＝単一指定）、2つ以上なら rebalance した mix を返す。
 * トグル不可（最後の1つ）のときは null。
 */
export function applyToggle(
  single: string,
  mix: MixEntry[],
  key: string,
): { single: string; mix: MixEntry[] } | null {
  const selected = mix.length > 0 ? mix.map(m => m.key) : [single]
  const next = toggleKey(selected, key)
  if (!next) return null
  if (next.length === 1) return { single: next[0], mix: [] }
  // 単一指定からの切り替えは「その1つが 100%」だったとみなして再配分する
  const prev = mix.length > 0 ? mix : [{ key: single, weight: 100 }]
  return { single, mix: rebalance(prev, next) }
}

/** 1項目の重みを 0〜100 の整数に丸めて差し替える（他の項目は変えない） */
export function setWeight(mix: MixEntry[], key: string, weight: number): MixEntry[] {
  const w = Number.isFinite(weight) ? Math.max(0, Math.min(100, Math.round(weight))) : 0
  return mix.map(m => (m.key === key ? { ...m, weight: w } : m))
}

export function mixSum(mix: Array<{ weight: number }>): number {
  return mix.reduce((a, b) => a + b.weight, 0)
}
